/**
 * Nav collections hook + browser-only module cache.
 *
 * On SSR, never mutate module-level cache (cross-request leak). Seed React Query
 * keys instead so Header reads per-request dehydrated data.
 */

import type { QueryClient } from '@tanstack/react-query';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getCollectionService } from '../../infrastructure/services/collection.service';
import type { CollectionEntity } from '../../domain/entities/collection.entity';

interface NavCollection {
  label: string;
  slug: string;
  imageUri?: string;
}

export type SaleCollectionNav = { slug: string; title: string };

export const NAV_COLLECTIONS_QUERY_KEY = ['nav-collections'] as const;
export const NAV_SALE_QUERY_KEY = ['nav-sale'] as const;

const FALLBACK_NAV: NavCollection[] = [
  { label: 'Best Sellers', slug: 'best-sellers' },
  { label: 'Shapewear', slug: 'shapewear' },
  { label: 'Bras', slug: 'bras' },
  { label: 'Briefs & Panties', slug: 'briefs-panties' },
];

const isSsr = import.meta.env.SSR === true;

function toNavCollection(c: CollectionEntity): NavCollection {
  return { label: c.title, slug: c.slug, imageUri: c.imageUri };
}

interface UseNavCollectionsResult {
  collections: NavCollection[];
  loading: boolean;
}

let cachedCollections: NavCollection[] | null = null;
let fetchPromise: Promise<NavCollection[]> | null = null;
let cachedSaleCollection: SaleCollectionNav | null = null;

/** Seed per-request RQ (+ browser module cache when not SSR). */
export function seedNavQueryData(
  queryClient: QueryClient,
  menuCollections: NavCollection[],
  saleCollection: SaleCollectionNav | null,
): void {
  if (menuCollections.length > 0) {
    queryClient.setQueryData<NavCollection[]>(NAV_COLLECTIONS_QUERY_KEY, menuCollections);
    setCachedCollections(menuCollections);
  }
  queryClient.setQueryData<SaleCollectionNav | null>(NAV_SALE_QUERY_KEY, saleCollection);
  setSaleCachedCollection(saleCollection);
}

/** Browser-only — no-op on SSR to avoid cross-request leaks. */
export function setSaleCachedCollection(data: SaleCollectionNav | null): void {
  if (isSsr) return;
  // Always assign (including null) so a cleared sale does not leave a stale link.
  cachedSaleCollection = data;
}

export function useSaleCollection(): SaleCollectionNav | null {
  const queryClient = useQueryClient();
  const seeded = queryClient.getQueryData<SaleCollectionNav | null>(['nav-sale']);

  const { data } = useQuery({
    queryKey: NAV_SALE_QUERY_KEY,
    queryFn: async (): Promise<SaleCollectionNav | null> => cachedSaleCollection,
    staleTime: 60_000,
    // Never refetch on SSR; client may keep dehydrated value.
    enabled: !isSsr,
  });

  // Prefer RQ / seeded value when set — including explicit `null` (cleared sale).
  // Do not use `??` here: null must win over a stale module cache.
  if (data !== undefined) return data;
  if (seeded !== undefined) return seeded;
  return !isSsr ? cachedSaleCollection : null;
}

/** True when browser module cache has menu collections. Always false on SSR. */
export function isNavCached(): boolean {
  if (isSsr) return false;
  return cachedCollections !== null;
}

export function getCachedSaleCollection(): SaleCollectionNav | null {
  if (isSsr) return null;
  return cachedSaleCollection;
}

/** Browser-only — no-op on SSR to avoid cross-request leaks. */
export function setCachedCollections(collections: NavCollection[]): void {
  if (isSsr) return;
  cachedCollections = collections;
}

function fetchMenuCollections(): Promise<NavCollection[]> {
  if (!isSsr && cachedCollections) return Promise.resolve(cachedCollections);
  if (fetchPromise) return fetchPromise;

  fetchPromise = getCollectionService()
    .getMenuCollections()
    .then((items) => {
      const mapped = items.length > 0 ? items.map(toNavCollection) : FALLBACK_NAV;
      if (!isSsr) cachedCollections = mapped;
      return mapped;
    })
    .catch(() => {
      if (!isSsr) cachedCollections = FALLBACK_NAV;
      return FALLBACK_NAV;
    })
    .finally(() => {
      fetchPromise = null;
    });

  return fetchPromise;
}

export function useNavCollections(): UseNavCollectionsResult {
  const queryClient = useQueryClient();
  const seeded = queryClient.getQueryData<NavCollection[]>(['nav-collections']);

  const { data, isPending, isFetched } = useQuery({
    queryKey: NAV_COLLECTIONS_QUERY_KEY,
    queryFn: fetchMenuCollections,
    staleTime: 60_000,
    // On SSR only use seeded dehydrated data — never hit network/module cache.
    enabled: !isSsr,
  });

  const browserCache = !isSsr ? cachedCollections : null;
  const collections: NavCollection[] =
    data && data.length > 0
      ? data
      : seeded && seeded.length > 0
        ? seeded
        : browserCache && browserCache.length > 0
          ? browserCache
          : FALLBACK_NAV;

  const hasResolved =
    (data != null && data.length > 0) ||
    (seeded != null && seeded.length > 0) ||
    (browserCache != null && browserCache.length > 0);
  const loading = !isSsr && !hasResolved && isPending && !isFetched;

  return { collections, loading };
}

export type { NavCollection };
